// src/components/buyerPersona/BuyerPersonaQuiz.jsx
import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaArrowRight, FaSpinner, FaCheck } from 'react-icons/fa';
import { useBuyerPersonaQuestions } from '../../hooks/useBuyerPersonaQuestions';
import { useBuyerPersonaQuiz } from '../../hooks/useBuyerPersonaQuiz';
import { useBuyerPersonaImage } from '../../hooks/useBuyerPersonaImage';
import { BUYER_PERSONA_CONFIG } from '../../config/buyerPersonaConfig';
import QuestionModule from './QuestionModule';
import ModuleProgress from './ModuleProgress';

const { MODULE_TITLES } = BUYER_PERSONA_CONFIG;

const BuyerPersonaQuiz = ({ mode = 'create', existingPersona = null, onComplete, onClose }) => {
    const [currentModule, setCurrentModule] = useState(0);
    const [responses, setResponses] = useState({});
    const [showResult, setShowResult] = useState(false);
    const [error, setError] = useState(null);

    // Hooks
    const { questions, loading: questionsLoading, getQuestionsByModule, getAvailableModules, isModuleComplete } = useBuyerPersonaQuestions();
    const { createBuyerPersona, updateBuyerPersona, loading: submitting } = useBuyerPersonaQuiz();
    const { startPolling } = useBuyerPersonaImage();

    // Debug logs (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
        console.log('BuyerPersonaQuiz - questions:', questions.length, 'loading:', questionsLoading);
        console.log('BuyerPersonaQuiz - modules:', getAvailableModules());
    }

    // Cargar respuestas existentes si estamos editando
    useEffect(() => {
        if (mode === 'edit' && existingPersona) {
            // Convertir el buyer persona existente a respuestas
            const existingResponses = {};
            // Aquí necesitarías mapear los campos del buyer persona a las preguntas
            // Por ahora, dejamos las respuestas vacías
            setResponses(existingResponses);
        }
    }, [mode, existingPersona]);

    const modules = getAvailableModules();
    const currentModuleName = modules[currentModule];
    const currentModuleQuestions = getQuestionsByModule(currentModuleName);

    // Debug logs (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
        console.log('BuyerPersonaQuiz - currentModule:', currentModule, 'currentModuleName:', currentModuleName);
        console.log('BuyerPersonaQuiz - currentModuleQuestions:', currentModuleQuestions.length);
    }

    // Verificar si todas las preguntas están respondidas
    const verifyAllQuestionsAnswered = () => {
        const unansweredQuestions = questions.filter(q => !responses[q.question]);
        return {
            allAnswered: unansweredQuestions.length === 0,
            unansweredQuestions
        };
    };

    // Manejar respuesta a una pregunta
    const handleAnswer = (question, answer) => {
        setResponses(prev => ({
            ...prev,
            [question]: answer
        }));
    };

    // Ir al siguiente módulo
    const handleNextModule = () => {
        if (currentModule < modules.length - 1) {
            setCurrentModule(currentModule + 1);
        }
    };

    // Ir al módulo anterior
    const handlePrevModule = () => {
        if (currentModule > 0) {
            setCurrentModule(currentModule - 1);
        }
    };

    // Ir a un módulo específico
    const handleGoToModule = (moduleIndex) => {
        setCurrentModule(moduleIndex);
    };

    // Enviar cuestionario
    const handleSubmit = async () => {
        try {
            // Verificar que todas las preguntas estén contestadas
            const verification = verifyAllQuestionsAnswered();
            if (!verification.allAnswered) {
                const errorMessage = `Faltan ${verification.unansweredQuestions.length} pregunta(s) por contestar. Por favor, completa todas las preguntas antes de enviar.`;
                setError(errorMessage);
                // Ir al primer módulo con preguntas sin contestar
                const firstUnansweredQuestion = verification.unansweredQuestions[0];
                const moduleIndex = modules.findIndex(module =>
                    getQuestionsByModule(module).some(q => q.question === firstUnansweredQuestion.question)
                );
                if (moduleIndex !== -1) {
                    setCurrentModule(moduleIndex);
                }
                return;
            }

            let result;
            if (mode === 'create') {
                result = await createBuyerPersona(responses);
            } else {
                result = await updateBuyerPersona(responses);
            }

            if (result) {
                onComplete(result);
            }
        } catch (error) {
            console.error('Error submitting buyer persona quiz:', error);
            setError('Hubo un error al procesar el cuestionario. Por favor, intenta de nuevo.');
        }
    };

    // Verificar si el módulo actual está completo
    const isCurrentModuleComplete = () => {
        const moduleQuestions = getQuestionsByModule(currentModuleName);
        return moduleQuestions.every(q => responses[q.question] !== undefined);
    };

    // Verificar si todos los módulos están completos
    const isAllModulesComplete = () => {
        return questions.every(q => responses[q.question] !== undefined);
    };

    // Calcular progreso general
    const totalProgress = Math.round(
        (Object.keys(responses).length / questions.length) * 100
    );

    if (questionsLoading) {
        return (
            <div className="text-center py-8">
                <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Cargando cuestionario...</h3>
                <p className="text-gray-600">Preparando las preguntas para crear tu buyer persona</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Error</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                    onClick={onClose}
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                    Cerrar
                </button>
            </div>
        );
    }

    // Si no hay preguntas disponibles después de cargar
    if (!questionsLoading && questions.length === 0) {
        if (process.env.NODE_ENV === 'development') {
            console.log('BuyerPersonaQuiz - No questions available, showing error message');
        }
        return (
            <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">
                    <FaSpinner className="mx-auto" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Módulo temporalmente no disponible
                </h3>
                <p className="text-gray-600 mb-4">
                    El módulo de Buyer Persona no está disponible en este momento.
                    Estará disponible próximamente.
                </p>
                <button
                    onClick={onClose}
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                    Cerrar
                </button>
            </div>
        );
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('BuyerPersonaQuiz - Rendering main content with questions:', questions.length);
    }
    return (
        <div className="flex flex-col h-full">
            {/* Progress Bar - Minimal */}
            <ModuleProgress
                modules={modules}
                currentModule={currentModule}
                responses={responses}
                totalProgress={totalProgress}
                onModuleClick={handleGoToModule}
                questions={questions}
            />

            {/* Main Content - Preguntas */}
            <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-4">
                <div className="max-w-2xl mx-auto">
                    <QuestionModule
                        module={currentModuleName}
                        title={MODULE_TITLES[currentModuleName]}
                        questions={currentModuleQuestions}
                        responses={responses}
                        onAnswer={handleAnswer}
                        moduleNumber={currentModule + 1}
                        totalModules={modules.length}
                    />
                </div>
            </div>

            {/* Navigation - Compact Footer */}
            <div className="bg-white border-t border-gray-200 p-2">
                <div className="max-w-2xl mx-auto flex items-center justify-between gap-2">
                    <button
                        onClick={handlePrevModule}
                        disabled={currentModule === 0}
                        className={`p-2 rounded-lg transition-colors ${currentModule === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 hover:bg-blue-50'
                            }`}
                    >
                        <FaArrowLeft className="w-5 h-5" />
                    </button>

                    {currentModule < modules.length - 1 ? (
                        <button
                            onClick={handleNextModule}
                            disabled={!isCurrentModuleComplete()}
                            className={`p-2 rounded-lg transition-colors ${!isCurrentModuleComplete()
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-blue-600 hover:bg-blue-50'
                                }`}
                        >
                            <FaArrowRight className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={!isAllModulesComplete() || submitting}
                            className={`px-4 py-2 rounded-lg transition-colors flex items-center ${!isAllModulesComplete() || submitting
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                        >
                            {submitting ? (
                                <>
                                    <FaSpinner className="animate-spin w-4 h-4 mr-2" />
                                    <span>Procesando...</span>
                                </>
                            ) : (
                                <>
                                    <FaCheck className="w-4 h-4 mr-2" />
                                    <span>Finalizar</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BuyerPersonaQuiz; 